import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

export type ToggleButtonSize = 'sm' | 'md';

@Component({
  selector: 'fc-toggle-button',
  template: `
    <div class="content">
      <ng-content></ng-content>
    </div>
  `,
  styles: `
    @use 'var' as *;

    :host {
      cursor: pointer;
      display: flex;
      align-items: center;
      position: relative;
      height: 40px;
      justify-content: center;
      color: $secondary-400;
      padding: 8px 12px;

      &.active {
        color: $grey-900;
        position: relative;
        cursor: default;
      }

      &.active.active-color-success,
      &.active.active-color-primary {
        color: $white;
      }

      &.full-width {
        flex: 1;
        justify-content: center;
      }

      &.size-sm {
        height: 30px;
        padding: 5px 8px;
      }

      /* The global .disabled utility sets pointer-events: none, which would
         swallow hover (tooltip) and click (mobile hint dialog) on the
         trigger — re-enable them; handleClick() ignores clicks itself. */
      &.disabled {
        opacity: 1;
        pointer-events: auto;
        cursor: not-allowed;
        color: $secondary-300;
      }
    }

    .content {
      z-index: 1;
      position: inherit;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* Beat the scoped .bg-* utility (0,2,0) the icon sets on itself, so
       projected icons always follow the toggle's text color. */
    :host .content ::ng-deep fc-icon {
      background-color: currentColor;
    }
  `,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.active]': 'selected()',
    '[class.disabled]': 'disabled()',
    '[class.full-width]': 'fullWidth',
    '[class.size-sm]': "size() === 'sm'",
    '[class.active-color-success]': "activeColor() === 'success'",
    '[class.active-color-primary]': "activeColor() === 'primary'",
    '(click)': 'handleClick()',
  },
})
export class ToggleButtonComponent {
  elRef = inject(ElementRef);
  disabled = input(false);
  value = input<any>(null);
  activeColor = input<'default' | 'success' | 'primary'>('default');
  selected = signal(false);
  size = signal<ToggleButtonSize>('md');
  onSelect = output<any>();
  fullWidth = false;

  protected handleClick(): void {
    if (this.disabled()) return;
    this.onSelect.emit(this.value());
  }
}
